import { Link } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { sort } from 'fast-sort';
import { EstateInputDataInstance_FieldsFragment, EstateType, EstateWorkItem } from 'queries';

import EstateInputDataInstanceDetail from './estate-input-data-instance-detail';

const useStyles = makeStyles(() => ({
  link: {
    cursor: 'pointer',
  },
  estateDetailTitle: {
    paddingTop: 5,
    fontWeight: 'bold',
  },
}));

type EstateWorkItemEstateDetailsProps = {
  estateWorkItem: EstateWorkItem;
  estateInputDataInstances: EstateInputDataInstance_FieldsFragment[];
  handleImpersonateView: (type: string, estateWorkItem: EstateWorkItem) => void;
};

const EstateWorkItemEstateDetails = ({
  estateWorkItem,
  estateInputDataInstances,
  handleImpersonateView,
}: EstateWorkItemEstateDetailsProps) => {
  const classes = useStyles();
  const estate = estateWorkItem.estate!;

  const noDataEstateInputDataInstances = estateInputDataInstances.filter(
    (estateInputDataInstance) => !estateInputDataInstance.data,
  );
  const hasDataEstateInputDataInstances = estateInputDataInstances.filter(
    (estateInputDataInstance) => !!estateInputDataInstance.data,
  );
  const sortedEstateInputDataInstances = noDataEstateInputDataInstances.concat(
    hasDataEstateInputDataInstances,
  );

  let targetLabel = '';
  switch (estate.type) {
    case EstateType.Truadmin:
      targetLabel = 'Decedent';
      break;
    case EstateType.Truplanning:
      targetLabel = 'Client';
      break;
    case EstateType.Truproxy:
      targetLabel = 'Ward';
      break;
    case EstateType.Trustadmin:
      targetLabel = 'Grantor';
      break;
  }

  return (
    <div>
      <Link
        className={classes.link}
        onClick={() => handleImpersonateView('estate', estateWorkItem)}
      >
        {estate.estateName}
      </Link>
      <div className={classes.estateDetailTitle}>Estate type: {estate.type}</div>
      {estate.type === EstateType.Trustadmin && (
        <>
          <div className={classes.estateDetailTitle}>Trust:</div>
          <div className={classes.estateDetailTitle}>
            {estate.estateTrustadminFields?.trustName}
          </div>
          {estate.estateTrustadminFields?.trustDate && (
            <div>Date: {estate.estateTrustadminFields.trustDate.slice(0, 10)}</div>
          )}
          {estate.estateTrustadminFields?.ein && (
            <div>EIN: {estate.estateTrustadminFields.ein}</div>
          )}
          <div>Grantor Trust: {estate.estateTrustadminFields?.isGrantorTrust ? 'Yes' : 'No'}</div>
          <div>
            Revocable Trust: {estate.estateTrustadminFields?.isRevocableTrust ? 'Yes' : 'No'}
          </div>
        </>
      )}
      <div className={classes.estateDetailTitle}>{targetLabel} Details:</div>
      <div>
        {estate.estateTargetFirstName} {estate.estateTargetLastName}
      </div>
      {(estate.type === EstateType.Truadmin ||
        estate.type === EstateType.Truplanning ||
        estate.type === EstateType.Truproxy) && (
        <>
          <div>{estate.estateTargetAddressStreet}</div>
          <div>
            {estate.estateTargetAddressCity && `${estate.estateTargetAddressCity}, `}
            {estate.estateTargetAddressState} {estate.estateTargetAddressZip}
          </div>
        </>
      )}
      <div>SSN: {estate.estateTargetSsn}</div>
      {estate.estateTargetDateOfBirth && (
        <div>DOB: {estate.estateTargetDateOfBirth.slice(0, 10)}</div>
      )}
      {estate.type === EstateType.Truadmin && estate.estateTargetDateOfDeath && (
        <div>DOD: {estate.estateTargetDateOfDeath.slice(0, 10)}</div>
      )}

      <div className={classes.estateDetailTitle}>Client:</div>
      <div>
        {estate.estateClientFirstName} {estate.estateClientLastName}
      </div>

      {estate.type === EstateType.Truplanning && (
        <>
          <div>{estate.estateTruplanningFields?.clientAddressStreet}</div>
          <div>
            {estate.estateTruplanningFields?.clientAddressCity &&
              `${estate.estateTruplanningFields?.clientAddressCity}, `}
            {estate.estateTruplanningFields?.clientAddressState}{' '}
            {estate.estateTruplanningFields?.clientAddressZip}
          </div>
        </>
      )}

      {estate.type === EstateType.Truadmin && (
        <>
          <div>{estate.estateTruadminFields?.clientAddressStreet}</div>
          <div>
            {estate.estateTruadminFields?.clientAddressCity &&
              `${estate.estateTruadminFields?.clientAddressCity}, `}
            {estate.estateTruadminFields?.clientAddressState}{' '}
            {estate.estateTruadminFields?.clientAddressZip}
          </div>
        </>
      )}

      <div>Email: {estate.estateClientEmailAddress}</div>
      <div>Phone: {estate.estateClientPhoneNumber}</div>

      {estate.type === EstateType.Truadmin && estate.estateTruadminFields?.clientFiduciaryRole && (
        <>
          <div>Fiduciary Role: {estate.estateTruadminFields?.clientFiduciaryRole}</div>
        </>
      )}

      {estate.type === EstateType.Trustadmin && (
        <>
          <div className={classes.estateDetailTitle}>Grantors:</div>
          {sort(estate.estateTrustadminFields?.estateGrantors || [])
            .by([
              {
                // primary Grantor comes first
                asc: (estateGrantor) => (estateGrantor.isPrimaryGrantor ? 0 : 1),
              },
              {
                asc: (estateGrantor) => estateGrantor.grantorFirstName,
              },
            ])
            .map((estateGrantor, index) => (
              <>
                <div className={classes.estateDetailTitle}>
                  {index + 1}) {estateGrantor.grantorFirstName} {estateGrantor.grantorLastName}{' '}
                  {estateGrantor.isPrimaryGrantor ? '(Primary)' : ''}
                </div>
                <div>SSN: {estateGrantor.grantorSsn}</div>
                <div>Still Alive: {estateGrantor.isGrantorStillAlive ? 'Yes' : 'No'}</div>
              </>
            ))}
        </>
      )}

      {estate.type === EstateType.Trustadmin && (
        <>
          <div className={classes.estateDetailTitle}>Trustees:</div>
          {sort(estate.estateTrustadminFields?.estateTrustees || [])
            .by([
              {
                // primary trustee comes first
                asc: (estateTrustee) => (estateTrustee.isPrimaryTrustee ? 0 : 1),
              },
              {
                asc: (estateTrustee) => estateTrustee.trusteeFirstName,
              },
            ])
            .map((estateTrustee, index) => (
              <>
                <div className={classes.estateDetailTitle}>
                  {index + 1}) {estateTrustee.trusteeFirstName} {estateTrustee.trusteeLastName}{' '}
                  {estateTrustee.isPrimaryTrustee ? '(Primary)' : ''}
                </div>
                {estateTrustee.isPrimaryTrustee && (
                  <>
                    <div>Email: {estateTrustee.trusteeEmail}</div>
                    <div>Phone: {estateTrustee.trusteePhoneNumber}</div>
                    <div>City: {estateTrustee.trusteeAddressCity}</div>
                    <div>State: {estateTrustee.trusteeAddressState}</div>
                    <div>Street: {estateTrustee.trusteeAddressStreet}</div>
                    <div>Zip: {estateTrustee.trusteeAddressZip}</div>
                  </>
                )}
              </>
            ))}
        </>
      )}
      {sortedEstateInputDataInstances.length > 0 && (
        <>
          <div className={classes.estateDetailTitle}>Required Matter Information:</div>
          {sortedEstateInputDataInstances.map((estateInputDataInstance) => (
            <EstateInputDataInstanceDetail estateInputDataInstance={estateInputDataInstance} />
          ))}
        </>
      )}
    </div>
  );
};

export default EstateWorkItemEstateDetails;
